import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { targetMetricsAPI } from '../../services/api';

/**
 * "October 2026 scores are waiting for sales." — shown at the top of the Employee
 * Score page.
 *
 * A parameter read from Sales & Targets has no figures for a month until that
 * month's sales are in, and where a project uploads its sales nothing in Sales &
 * Targets says "this month is complete": an admin says so, once, by pressing the
 * button here. Until then the month is held for every role that carries such a
 * parameter, and for the managers above them.
 *
 * The page opens on the newest month that HAS scores, so the month that is waiting
 * is rarely the one on screen. That is why this bar does not follow the month
 * picker: it lists every waiting month, whichever month is selected.
 */

/** One parameter waiting for an admin, from `meta.sales_feed_waiting` of the score response. */
export interface SalesFeedWaiting {
  target_metric_id: number;
  name: string;
  /** The waiting month: YYYY-MM-01 (YYYY-MM is read the same way). */
  month: string;
}

interface Props {
  /** `meta.sales_feed_waiting`. The API sends it only to people who may save the configuration. */
  waiting: SalesFeedWaiting[];
  /** Called after a press, whatever it managed to store, so the scores and this bar are read again. */
  onReleased: () => void | Promise<void>;
}

const firstOfMonth = (month: string): string => `${String(month).slice(0, 7)}-01`;

const monthLabel = (month: string, withYear: boolean): string => {
  const d = new Date(`${firstOfMonth(month)}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? String(month)
    : d.toLocaleDateString('en-US', { month: 'long', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC' });
};

// What a refused press is reported as when the API gave no sentence of its own.
const NOT_READ = 'Could not read Sales & Targets.';

export default function SalesFeedWaitingBar({ waiting, onReleased }: Props) {
  // The month whose button was pressed; every button is off while one month is being stored.
  const [busyMonth, setBusyMonth] = useState<string | null>(null);
  // month → what the API answered when a parameter of that month could not be stored.
  const [problems, setProblems] = useState<Record<string, string>>({});

  // One bar per month, oldest first: an older month has been waiting longer.
  const byMonth = new Map<string, SalesFeedWaiting[]>();
  waiting.forEach((w) => {
    const month = firstOfMonth(w.month);
    byMonth.set(month, [...(byMonth.get(month) ?? []), w]);
  });
  const months = [...byMonth.keys()].sort();
  if (months.length === 0) return null;

  // Stores every waiting parameter of the month. Resolves to the first refusal, if any.
  const releaseAll = async (month: string): Promise<string | null> => {
    // "Score everyone on zero?" is asked once for the month, not once per parameter.
    let zeroConfirmed = false;
    let problem: string | null = null;
    // In turn, not together: each one reads the same month of Sales & Targets.
    for (const item of byMonth.get(month) ?? []) {
      try {
        await targetMetricsAPI.releaseSalesFeed(item.target_metric_id, month);  // codeguardian-ignore: serial by design (sequential dependency / ordered aggregation / rate-limited I/O)
      } catch (err: any) {
        const refused = err?.response?.data;
        if (refused?.code !== 'NO_SALES') {
          // Said as the API said it; the remaining parameters are still tried.
          problem = problem ?? (refused?.message || NOT_READ);
          continue;
        }
        // Nothing sold may be the truth for the month. Only a person can say so.
        if (!zeroConfirmed) {
          zeroConfirmed = window.confirm(`No ${monthLabel(month, false)} sales found. Score everyone on zero?`);
        }
        // Declined: the month stays waiting, and so do its other parameters.
        if (!zeroConfirmed) break;
        try {
          await targetMetricsAPI.releaseSalesFeed(item.target_metric_id, month, { allow_empty: true });  // codeguardian-ignore: serial by design (sequential dependency / ordered aggregation / rate-limited I/O)
        } catch (again: any) {
          problem = problem ?? (again?.response?.data?.message || NOT_READ);
        }
      }
    }
    return problem;
  };

  const handlePress = async (month: string) => {
    if (busyMonth !== null) return;
    setBusyMonth(month);
    setProblems((prev) => {
      const next = { ...prev };
      delete next[month];
      return next;
    });
    try {
      const problem = await releaseAll(month);
      if (problem) setProblems((prev) => ({ ...prev, [month]: problem }));
      // Whatever was stored, read the scores again: a month that went through
      // leaves this bar, one that did not stays on it.
      await onReleased();
    } finally {
      setBusyMonth(null);
    }
  };

  return (
    <div className="space-y-2">
      {months.map((month) => (
        <div
          key={month}
          className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900"
        >
          <span className="font-medium">{monthLabel(month, true)} scores are waiting for sales.</span>
          <button
            type="button"
            onClick={() => handlePress(month)}
            disabled={busyMonth !== null}
            // Which parameters the press is for, for whoever wants to know before pressing.
            title={(byMonth.get(month) ?? []).map((w) => w.name).filter(Boolean).join(', ') || undefined}
            className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {busyMonth === month && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Use {monthLabel(month, false)} sales
          </button>
          {problems[month] && (
            <span role="alert" className="text-sm text-rose-700">{problems[month]}</span>
          )}
        </div>
      ))}
    </div>
  );
}
